import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { getLastDateRange } from 'src/utils/dateTimeUtils';
import { DefaultListingQuery } from 'src/utils/eventQueryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  CatalogClient,
  DateTimeRange,
  EventWithData,
  ListingQueryV2,
  Performer,
  Venue,
} from 'src/WebApiController';

/**
 * Scoped event list for the campaign manager Boost ingress.
 *
 * Returns the account's active inventory events only: it queries listing
 * counts with `eventHasUnsoldTickets: true` (mirroring the Sponsored Listings
 * page's date window — events from the last 30 days forward) and keeps just
 * the mapped (viagogo) events from the ambient page catalog that have unsold
 * tickets. This guarantees the boost event search can only ever display
 * events this account actively holds inventory for.
 */
export const useBoostIngressScopedEvents = (): {
  scopedEvents: EventWithData[] | undefined;
  isLoading: boolean;
} => {
  const { data: catalogData } = useCatalogDataContext();
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const activeInventoryEventIdsQuery = useQuery({
    queryKey: [
      'useBoostIngressScopedEvents.getActiveInventoryEventIds',
      activeAccountWebClientConfig.activeAccountId,
    ],
    queryFn: () =>
      tryInvokeApi(
        async () => {
          const client = new CatalogClient(activeAccountWebClientConfig);
          const counts = await client.getCatalogCountForListings({
            ...DefaultListingQuery,
            eventHasUnsoldTickets: true,
            eventDates: {
              start: getLastDateRange(
                { days: 30 },
                new Date()
              ).start.toISOString(),
              end: null,
            } as DateTimeRange,
          } as ListingQueryV2);

          const virtIds = new Set<string>();
          counts?.entityCnts?.forEach((counts) => {
            if (counts.viagVirtId != null) {
              virtIds.add(counts.viagVirtId);
            }
          });
          return virtIds;
        },
        (error) => {
          showErrorDialog('CatalogClient.getCatalogCountForListings', error);
        }
      ),
    enabled: activeAccountWebClientConfig.activeAccountId != null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const activeInventoryEventIds = activeInventoryEventIdsQuery.data;

  const scopedEvents = useMemo(() => {
    if (!catalogData?.events || activeInventoryEventIds == null) {
      return undefined;
    }
    // Note: pending/rejected (unmapped) events are intentionally included —
    // the shared EventSearch "Show pending/rejected events" filter controls
    // their visibility, and unmapped events are classified as ineligible
    // downstream since they cannot be boosted without a viagogo event id
    return Object.values(catalogData.events).filter((eventWithData) =>
      activeInventoryEventIds.has(eventWithData.event?.virtId ?? '')
    );
  }, [activeInventoryEventIds, catalogData]);

  return {
    scopedEvents,
    isLoading:
      activeInventoryEventIdsQuery.isLoading ||
      (activeInventoryEventIds != null && scopedEvents === undefined),
  };
};

/**
 * Performer/venue records for the scoped events, fetched via getCatalogSlim.
 *
 * The Sponsored Listings page catalog only carries entity records for events
 * that were expanded in the page UI (or historic campaign events), so the
 * ambient context alone cannot resolve performers/venues for arbitrary
 * scoped events. This fetches them explicitly so the boost search dropdown
 * can surface the Events/Performers/Venues sections.
 */
export const useBoostIngressEntityCatalog = (
  scopedEvents: EventWithData[] | undefined
):
  | {
      performers: Record<string, Performer>;
      venues: Record<string, Venue>;
    }
  | undefined => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const virtIds = useMemo(
    () => scopedEvents?.map((e) => e.event.virtId).toSorted() ?? [],
    [scopedEvents]
  );

  const entityCatalogQuery = useQuery({
    queryKey: [
      'useBoostIngressEntityCatalog.getCatalogSlim',
      activeAccountWebClientConfig.activeAccountId,
      virtIds,
    ],
    queryFn: () =>
      tryInvokeApi(
        async () => {
          const client = new CatalogClient(activeAccountWebClientConfig);
          return await client.getCatalogSlim(virtIds);
        },
        (error) => {
          showErrorDialog('CatalogClient.getCatalogSlim', error);
        }
      ),
    enabled:
      scopedEvents != null &&
      virtIds.length > 0 &&
      activeAccountWebClientConfig.activeAccountId != null,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const slim = entityCatalogQuery.data;

  return useMemo(() => {
    if (slim == null) {
      return undefined;
    }
    const performers: Record<string, Performer> = {};
    slim.performers.forEach((performer) => {
      if (performer.viagId != null) {
        performers[performer.viagId.toString()] = performer;
      }
    });
    const venues: Record<string, Venue> = {};
    slim.venues.forEach((venue) => {
      if (venue.viagId != null) {
        venues[venue.viagId.toString()] = venue;
      }
    });
    return { performers, venues };
  }, [slim]);
};
